import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Eye, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { DatePicker } from '../../components/ui/date-picker';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { DataTable } from '../../components/ui/data-table';
import AttendanceStats from '../../components/attendance/AttendanceStats';
import { Student, Batch } from '../../types/common';
import { useToast } from '../../hooks/use-toast';
import Header from '../../components/layout/Header';
import { app } from "../../firebase";
import { getAuth } from "firebase/auth";
import {
  collection,
  onSnapshot,
  query,
  where,
  getFirestore,
  setDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { useNavigate } from 'react-router-dom';

const Attendance = () => {
  const { toast } = useToast();
  const [date, setDate] = useState<Date>(new Date());
  const [selectedBatch, setSelectedBatch] = useState<string>("");
  const [batches, setBatches] = useState<Batch[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [showPresent, setShowPresent] = useState<boolean>(true);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [courseTodayClasses, setCourseTodayClasses] = useState<any>(null);
  const [courseAttendance, setCourseAttendance] = useState<any>(null);
  const db = getFirestore(app);
  const auth = getAuth(app);
  const navigate = useNavigate();

  const [loading, setIsLoading] = useState(false);

  // Load active batches from Firestore  
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "courseBatches"),
      (querySnapshot) => {
        const result: any = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })).sort(sortByBatchName).filter((x: any) => x.isActive == true);
        console.log(result);
        setBatches(result);
      },
      (error) => {
        toast({ title: error.message, description: "" });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [db, toast]);

  // Load students whenever the selected batch changes  
  useEffect(() => {
    const fetchData = async () => {
      if (selectedBatch) {
        // Await the results of each function call  
        const students: any = await loadStudents();
        const todayClass = await loadCourseTodayClass();
        const attendance = await loadCourseAttendance();
        if (students?.length > 0 && todayClass && attendance) {
          const todayDate = new Date(date).toLocaleDateString('en-GB'); // Format as DD-MM-YYYY  
          const formattedDate = todayDate.split('/').join('-'); // Convert to DD-MM-YYYY  
          checkAttendanceAndMarkForStudents(students, attendance, formattedDate);
        } else {
          setStudents(prev => ([]));
        }
      }
    };

    fetchData();
  }, [selectedBatch, showPresent]);


  useEffect(() => {
    const checkUserLoggedIn = async () => {
      setIsLoading(true);
      const userData = JSON.parse(localStorage?.getItem('user')!) ?? null;

      if (userData && userData.accessToken) {
        try {
          const studentQuery = query(
            collection(db, "users"),
            where("emailId", "==", userData.emailId)
          );
          const studentSnapshot = await getDocs(studentQuery);


          const studentDoc = studentSnapshot.docs[0];
          const studentDetails: any = { id: studentDoc.id, ...studentDoc.data() };
          if (studentDetails?.role !== "MASTERADMIN" && studentDetails?.role !== "INSTRUCTOR" && studentDetails?.role !== "ADMIN" && studentDetails?.role !== "SUPERADMIN") {
            setTimeout(() => {
              toast({
                title: "Access Denied",
                description: "Only Admins have access to ByteSplash Registration Portal.",
              });
            }, 2000)
            localStorage.clear();
            window.location.href = "/login";
          }
          setIsLoading(false);
        } catch (error) {
          console.error("Error during fetching student or courses:", error);
          navigate("/login");
        }
      } else {
        navigate("/login");
      }
    };

    checkUserLoggedIn();
  }, [setIsLoading]);

  // Load today's classes based on the selected batch  
  const loadCourseTodayClass = async () => {
    try {
      // Get the batch data using selectedBatch ID  
      const batchQuery = query(collection(db, 'courseTodayClasses'), where('batchId', '==', selectedBatch));
      const batchSnapshot = await getDocs(batchQuery);

      if (!batchSnapshot.empty) {
        const batchData = batchSnapshot.docs[0].data(); // Get the first document's data  
        // Assuming batchData contains a "courseTodayClasses" field  
        setCourseTodayClasses(prev => ({ ...batchData || null }));
        return batchData;

      } else {
        console.error("Batch document does not exist.");
        setCourseTodayClasses(null); // Reset to empty if no document found  
      }
    } catch (error) {
      console.error("Error loading today's classes:", error);
    }
  };


  const checkAttendanceAndMarkForStudents = (studentsData, courseAttendanceData, date) => {

    const studentEmails = studentsData.map(student => student.emailId);
    const results = checkTodaysAttendance(courseAttendanceData, studentEmails, date);

    // Update each student's attendance based on the results  
    studentsData.forEach(student => {
      // Check if the student's email exists in the results  
      if (results[student.emailId]) {
        student.attendance = true; // Mark as attended  
      } else {
        student.attendance = false; // Mark as not attended  
      }
    });

    console.log(studentsData);
    const studentsList = studentsData?.filter(x => x.isInternal);
    setStudents((prev) => [...studentsList])
  };


  const checkTodaysAttendance = (courseAttandance: any, emailIds: any, date: any) => {
    const results = {};

    for (const emailId of emailIds) {
      results[emailId] = false; // Initialize results for each emailId  

      const classInfo = courseAttandance[courseAttandance.batchName]
      for (const subject of classInfo) {
        //   console.log(subject)
        const conductedSessions = subject[subject.className].conducted;
        if (conductedSessions[date]) {
          const attendees = conductedSessions[date];
          if (attendees.includes(emailId)) {
            results[emailId] = true; // Update result if emailId is found  
            break; // Exit the loop if found  
          }
        }
      }
    }

    return results; // Return the results object  
  }


  // Load attendance for the selected batch and current date  
  const loadCourseAttendance = async () => {
    try {
      // Get the attendance data using selectedBatch ID  
      const attendanceQuery = query(collection(db, 'courseAttandance'), where('batchId', '==', selectedBatch));
      const attendanceSnapshot = await getDocs(attendanceQuery);

      if (!attendanceSnapshot.empty) {
        const attendanceData = attendanceSnapshot.docs[0].data(); // Get the first document's data  
        setCourseAttendance(prev => ({ ...attendanceData || null })); // Assuming there's a field for records  
        return attendanceData;
      } else {
        console.error("Attendance document does not exist.");
        setCourseAttendance(null); // Reset to empty if no document found  
      }
    } catch (error) {
      console.error("Error loading attendance:", error);
    }
  };

  function sortByBatchName(x: any, y: any) {
    if (x.batchName > y.batchName) return 1;
    else if (x.batchName < y.batchName) return -1;
    else return 0;
  }

  // Function to load students from Firestore based on the selected batch  
  const loadStudents = async () => {
    try {
      // Get the batch data using selectedBatch ID  
      const batchDocRef = doc(db, 'courseBatches', selectedBatch);
      const batchSnapshot = await getDoc(batchDocRef);

      if (batchSnapshot.exists()) {
        const batchData = batchSnapshot.data();

        // Assuming that the student emails are stored in 'students' array in the batch document  
        const studentEmails = batchData.students; // array of student email IDs  

        // Fetching students details using the email IDs  
        const studentPromises = studentEmails.map(email => {
          const studentCollectionRef = collection(db, 'students');
          const studentQuery = query(studentCollectionRef, where('emailId', '==', email));
          return getDocs(studentQuery).then(snapshot => {
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          });
        });

        // Wait for all student fetch promises to resolve  
        const allStudentDocs = await Promise.all(studentPromises);
        // Flatten the array of student documents  
        const studentList = allStudentDocs.flat();

        // Filter students based on attendance  
        // setStudents(prev => studentList.filter(x => x.isInternal));
        console.log(studentList);
        return studentList;
      } else {
        console.error("No batch data found for the selectedBatch ID");
        setStudents([]); // Reset the students if no batch found  
      }
    } catch (error) {
      console.error("Error loading students:", error);
      // Optional: Optionally display a toast message or handle the error accordingly  
      toast({ title: "Error fetching students", description: error.message });
      setStudents([]); // Reset the students on error  
    }
  };

  const handleDateChange = (newDate: Date | undefined) => {
    const todayDate = new Date(newDate).toLocaleDateString('en-GB'); // Format as DD-MM-YYYY  
    const formattedDate = todayDate.split('/').join('-'); // Convert to DD-MM-YYYY  
    if (formattedDate) {
      setDate(newDate);
      checkAttendanceAndMarkForStudents(students, courseAttendance, formattedDate);
    }
  };

  const handleBatchChange = (value: string) => {
    setSelectedBatch(value);
    setSelectedStudent(null);
  };

  const handleToggleAttendance = async (studentId: string, attendance: boolean) => {
    const studentDocRef = doc(db, 'students', studentId);
    await updateDoc(studentDocRef, { attendance });

    toast({
      title: `Attendance ${attendance ? 'Marked' : 'Unmarked'}`,
      description: `Student has been marked as ${attendance ? 'present' : 'absent'}.`,
    });

    loadStudents();
  };


  // Generic function to mark attendance for any subject dynamically  
  function markAttendance(studentEmail, event, batchId) {

    setStudents(prevStudents =>
      prevStudents.map(student => ({
        ...student,
        attendance: student.emailId === studentEmail ? !!event ? true : false : student.attendance// Update attendance based on results  
      }))
    );

    const todayDate = new Date().toLocaleDateString('en-GB'); // Format as DD-MM-YYYY  
    const formattedDate = todayDate.split('/').join('-'); // Convert to YYYY-MM-DD  

    // Loop through the subjects in courseTodayClasses to get dynamic subjects  
    courseTodayClasses?.subjects?.forEach(subjectName => {
      let subjectFound = false;

      // Iterate through the existing course attendance data  
      for (let subject of courseAttendance[courseAttendance.batchName]) {
        if (subject.className === subjectName) {
          subjectFound = true; // Subject exists  

          const conductedDays = subject[subjectName].conducted;

          // Check if today's date exists in the attendance records  
          if (!conductedDays[formattedDate]) {
            conductedDays[formattedDate] = []; // Initialize an array for the new date  
          }

          // Add the student's email to today's attendance if not already recorded  
          if (!conductedDays[formattedDate].includes(studentEmail)) {
            if (event) {
              conductedDays[formattedDate].push(studentEmail);
            }
            else {
              conductedDays[formattedDate] = ([...conductedDays[formattedDate].filter(x => x !== studentEmail)]);
            }
            console.log(`Attendance marked for ${studentEmail} in ${subjectName} on ${formattedDate}`);
          } else {
            if (!event) {
              conductedDays[formattedDate] = ([...conductedDays[formattedDate].filter(x => x !== studentEmail)]);
            }
            console.log(`Attendance for ${studentEmail} in ${subjectName} on ${formattedDate} already recorded`);
          }
        }

      }
      // If the subject does not exist, create a new entry  
      if (!subjectFound) {
        const newSubjectAttendance = {
          className: subjectName,
          [subjectName]: {
            conducted: {
              [formattedDate]: event ? [studentEmail] : [] // Add the student for today  
            }
          }
        };
        courseAttendance[courseTodayClasses.batchName].push(newSubjectAttendance);
      }

    });

    setCourseAttendance(prev => ({ ...courseAttendance }));
    console.log(courseAttendance);
  }

  const toggleAttendanceFilter = () => {
    setShowPresent(!showPresent);
  };

  const handleViewStudent = (student: Student) => {
    setSelectedStudent(student);
  };

  const handleSaveSubmit = async () => {
    try {
      const courseAttandanceRef = doc(db, "courseAttandance", courseAttendance?.id); // Get a reference to the course document  
      // Use setDoc to update the document in Firestore  
      await setDoc(courseAttandanceRef, courseAttendance); // Update the course data in Firestore  
      // Save attendance logic, possibly save all updated student objects  
      toast({
        title: "Attendance Saved",
        description: "Attendance has been successfully recorded.",
        className: "bg-bytesplash-blue text-white",
      });
    } catch (error) {
      console.error("Error updating course:", error);
      toast({
        title: "Error",
        description: "Failed to update the course. Please try again.",
        variant: "destructive"
      });
    } finally {
    }
  };

  const getBatchColor = (batchName: string | undefined): string => {
    if (!batchName) return "bg-gray-100 text-gray-800";
    if (batchName.includes("DFS")) return "bg-red-100 text-red-800";
    if (batchName.includes("JFS")) return "bg-blue-100 text-blue-800";
    if (batchName.includes("PFS")) return "bg-green-100 text-green-800";
    return "bg-yellow-100 text-yellow-800";
  };

  const getBatchName = () => {
    return batches?.find(x => x.id == selectedBatch)?.batchName;
  }
  const columns: any = [
    { id: "Id", header: "ID", accessorKey: "studentIdNo" },
    { id: "name", header: "Student Name", accessorKey: "studentsName" },
    {
      id: "batch", header: "Batch", accessorKey: "batch",
      cell: (student: Student) => (
        <span className={`px-2 py-1 bg-[#0A7AC0FF] text-white rounded-full text-xs`}>
          {getBatchName()}
        </span>
      )
    },
    { id: "mobile", header: "Mobile", accessorKey: "mobileNumber" },
    {
      id: "attendance", header: "Mark Attendance",
      cell: ({ row }) => {
        const data = row.original;

        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={data?.attendance}
              onCheckedChange={(checked) => markAttendance(data?.emailId, checked, selectedBatch)}
              className={data?.attendance ? "bg-green-500" : "bg-gray-200"}
            />
            <span className={data?.attendance ? "text-green-500" : "text-red-500"}>
              {data.attendance ? "Present" : "Absent"}
            </span>
          </div>)
      }
    },
    {
      id: "actions", header: "Actions",
      cell: (student: Student) => (
        <Button variant="ghost" size="sm" onClick={() => handleViewStudent(student)}>
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
      )
    },
  ];

  const selectedBatchName = batches.find(b => b.id === selectedBatch)?.batchName || "";
  const totalStudents = students.length;
  const presentCount = students.filter(student => student.attendance).length;
  const absentCount = totalStudents - presentCount;

  return (
    <div className="h-full flex flex-col bg-gray-50 p-0">
      <Header title="Attendance Management" subtitle="Manage student daily attendance and more" />
      <div className='p-4'>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 mt-8 ">
          <div className="col-span-1">
            <span className="text-sm font-medium">Filter Attendance By Date</span>
            <DatePicker value={date} onChange={handleDateChange} className="w-full" />
          </div>

          <div className="col-span-1 md:col-span-1 flex flex-col items-center justify-center">
            <div className="text-center">
              <span className="text-bytesplash-blue font-semibold bg-bytesplash-soft-blue rounded-full px-4 py-1 inline-block">
                {format(date, 'MMM d, yyyy')}
              </span>
              <h1 className="text-2xl font-bold text-bytesplash-blue mt-2">Attendance</h1>
            </div>
          </div>

          <div className="col-span-1 md:col-span-1">
            <span className="text-sm font-medium">Select Batch</span>
            {totalStudents > 0 && (
              <span className="bg-bytesplash-blue text-white text-sm rounded-full w-6 h-6 flex items-center justify-center">
                {totalStudents}
              </span>
            )}
            <Select value={selectedBatch} onValueChange={handleBatchChange}>
              <SelectTrigger className="border-2 hover:border-bytesplash-blue">
                <SelectValue placeholder="Select a batch" />
              </SelectTrigger>
              <SelectContent>
                {batches.map((batch) => (
                  <SelectItem key={batch.id} value={batch.id}>
                    {batch.batchName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="flex items-center space-x-4 bg-white px-4 py-2 rounded-lg shadow-sm border w-full md:w-auto">
              <span className="text-sm font-medium text-gray-700">Batch:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getBatchColor(selectedBatchName)}`}>
                {selectedBatchName || "No batch selected"}
              </span>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Show:</span>
                <Switch
                  checked={showPresent}
                  onCheckedChange={toggleAttendanceFilter}
                  className={showPresent ? "bg-green-500" : "bg-red-500"}
                />
                <span className={`text-sm font-medium ${showPresent ? "text-green-600" : "text-red-600"}`}>
                  {showPresent ? "Present" : "Absent"}
                </span>
                <span className="bg-gray-100 px-2 py-1 rounded-full text-xs font-medium text-gray-600">
                  {showPresent ? presentCount : absentCount}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button className="bg-bytesplash-blue hover:bg-[#02436EFF] shadow-sm" onClick={handleSaveSubmit}>
              <Save className="mr-2 h-4 w-4" /> Save & Submit
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-1 gap-6 ">
          <div className="col">
            <Card className="shadow-md">
              <CardContent className="p-4 h-full">
                <DataTable
                  columns={columns}
                  data={students}
                  noResultsMessage={`No ${showPresent ? 'present' : 'absent'} students found`}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attendance;  